import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  RefreshCw,
  Mail,
  Building2,
  Phone,
  MessageSquare,
  LogOut,
  KeyRound,
  Plus,
  Pencil,
  Trash2,
  Briefcase,
  FileText,
  Inbox as InboxIcon,
  UserCog,
  Check,
  Ban,
} from "lucide-react";
import { toast } from "sonner";
import {
  listContacts,
  listClients,
  deleteClient,
  updateClient,
  adminListBlog,
  adminDeleteBlog,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ADMIN, ADMIN_TABS, ADMIN_CLIENTS, ADMIN_BLOG } from "@/constants/testIds";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import ClientEditorModal from "@/components/ClientEditorModal";
import BlogEditorModal from "@/components/BlogEditorModal";

const EASE = [0.76, 0, 0.24, 1];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
};

const TABS = [
  { id: "enquiries", label: "Enquiries", icon: InboxIcon, testId: ADMIN_TABS.enquiries },
  { id: "clients", label: "Hiring partners", icon: UserCog, testId: ADMIN_TABS.clients },
  { id: "blog", label: "Insights", icon: FileText, testId: ADMIN_TABS.blog },
];

export default function AdminPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [clientEditor, setClientEditor] = useState({ open: false, initial: null });
  const [blogEditor, setBlogEditor] = useState({ open: false, initial: null });

  const handleAuthErr = useCallback(
    (e) => {
      if (e?.response?.status === 401) {
        signOut();
        navigate("/admin/login", { replace: true });
        return true;
      }
      return false;
    },
    [navigate, signOut]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (tab === "enquiries") setItems(await listContacts());
      else if (tab === "clients") setClients(await listClients());
      else if (tab === "blog") setPosts(await adminListBlog());
    } catch (e) {
      if (handleAuthErr(e)) return;
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [tab, handleAuthErr]);

  useEffect(() => {
    load();
  }, [load]);

  const onLogout = () => {
    signOut();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  const onDeleteClient = async (c) => {
    if (!window.confirm(`Remove access for ${c.name} (${c.email})?`)) return;
    try {
      await deleteClient(c.id);
      toast.success(`${c.name} removed.`);
      setClients((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Delete failed.");
    }
  };

  const onToggleActive = async (c) => {
    try {
      const updated = await updateClient(c.id, { is_active: !c.is_active });
      setClients((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
      toast.success(updated.is_active ? "Enabled." : "Disabled.");
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Update failed.");
    }
  };

  const onDeletePost = async (p) => {
    if (!window.confirm(`Delete essay "${p.title}"?`)) return;
    try {
      await adminDeleteBlog(p.id);
      toast.success("Essay deleted.");
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Delete failed.");
    }
  };

  const onClientSaved = (saved) => {
    setClients((prev) => {
      const idx = prev.findIndex((x) => x.id === saved.id);
      if (idx === -1) return [saved, ...prev];
      const copy = [...prev];
      copy[idx] = saved;
      return copy;
    });
  };

  const onPostSaved = (saved) => {
    setPosts((prev) => {
      const idx = prev.findIndex((x) => x.id === saved.id);
      if (idx === -1) return [saved, ...prev];
      const copy = [...prev];
      copy[idx] = saved;
      return copy;
    });
  };

  return (
    <div className="pt-32 pb-24 bg-[var(--bg)] min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-8">
          <div>
            <p className="overline mb-4">Admin · Console</p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95] text-[var(--ink)]"
            >
              Console.
            </motion.h1>
            <p className="mt-4 text-[var(--ink-80)]">
              {tab === "enquiries" && (
                <>
                  {items.length} enquir{items.length === 1 ? "y" : "ies"}
                </>
              )}
              {tab === "clients" && (
                <>
                  {clients.length} hiring partner{clients.length === 1 ? "" : "s"}
                </>
              )}
              {tab === "blog" && (
                <>
                  {posts.length} essay{posts.length === 1 ? "" : "s"} ·{" "}
                  {posts.filter((p) => p.is_published).length} live
                </>
              )}
              {user?.email && (
                <>
                  {" "}
                  · <span className="text-[var(--ink-50)]">{user.email}</span>
                </>
              )}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={load}
              disabled={loading}
              className="btn-ghost"
              data-testid={ADMIN.refresh}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setPwOpen(true)}
              className="btn-ghost"
              data-testid={ADMIN.changePwOpen}
            >
              <KeyRound className="w-4 h-4" />
              <span>Change password</span>
            </button>
            <button
              onClick={onLogout}
              className="btn-ghost"
              data-testid={ADMIN.logout}
            >
              <LogOut className="w-4 h-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--line-strong)] mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={t.testId}
                className={`px-4 md:px-6 py-3 flex items-center gap-2 text-xs uppercase tracking-[0.22em] border-b-2 -mb-px transition-colors ${
                  active
                    ? "border-[var(--accent)] text-[var(--ink)]"
                    : "border-transparent text-[var(--ink-70)] hover:text-[var(--ink)]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="border border-red-500/40 bg-red-500/5 text-red-700 p-4 mb-6">
            {error}
          </div>
        )}

        {/* Enquiries tab */}
        {tab === "enquiries" && (
          <>
            {!loading && items.length === 0 && (
              <div
                data-testid={ADMIN.empty}
                className="border border-[var(--line-strong)] bg-[var(--bg-2)] py-24 text-center"
              >
                <p className="font-serif-accent italic text-[var(--accent)] text-2xl mb-2">
                  Nothing yet.
                </p>
                <p className="text-[var(--ink-80)]">
                  Submissions from the contact form will appear here.
                </p>
              </div>
            )}

            {items.length > 0 && (
              <div
                className="border border-[var(--line-strong)]"
                data-testid={ADMIN.table}
              >
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--line-strong)] text-[10px] uppercase tracking-[0.24em] text-[var(--ink-50)] bg-[var(--bg-2)]">
                  <div className="col-span-3">Person</div>
                  <div className="col-span-3">Company / Service</div>
                  <div className="col-span-4">Message</div>
                  <div className="col-span-2 text-right">Received</div>
                </div>
                <div>
                  {items.map((it, i) => (
                    <motion.div
                      key={it.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.02 * i }}
                      className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-6 border-b border-[var(--line)] hover:bg-[var(--bg-2)] transition-colors"
                      data-testid={ADMIN.row(it.id)}
                    >
                      <div className="col-span-3">
                        <p className="text-[var(--ink)] font-medium">{it.name}</p>
                        <a
                          href={`mailto:${it.email}`}
                          className="link-slide text-[var(--ink-80)] text-sm inline-flex items-center gap-1.5 break-all"
                        >
                          <Mail className="w-3 h-3" /> {it.email}
                        </a>
                        {it.phone && (
                          <p className="text-[var(--ink-50)] text-sm mt-1 inline-flex items-center gap-1.5">
                            <Phone className="w-3 h-3" /> {it.phone}
                          </p>
                        )}
                      </div>
                      <div className="col-span-3">
                        {it.company && (
                          <p className="text-[var(--ink)] inline-flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5" /> {it.company}
                          </p>
                        )}
                        {it.service && (
                          <p className="mt-2 text-[10px] uppercase tracking-[0.24em] text-[var(--accent)] border border-[var(--accent)]/40 inline-block px-2 py-1">
                            {it.service}
                          </p>
                        )}
                      </div>
                      <div className="col-span-4">
                        <p className="text-[var(--ink-80)] text-sm leading-relaxed inline-flex items-start gap-1.5">
                          <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span className="whitespace-pre-wrap">{it.message}</span>
                        </p>
                      </div>
                      <div className="col-span-2 md:text-right">
                        <p className="text-[var(--ink-50)] text-xs uppercase tracking-[0.2em]">
                          {formatDate(it.created_at)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Clients tab */}
        {tab === "clients" && (
          <div data-testid={ADMIN_CLIENTS.list}>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setClientEditor({ open: true, initial: null })}
                className="btn-pill text-sm"
                data-testid={ADMIN_CLIENTS.create}
              >
                <Plus className="w-4 h-4" />
                <span>New hiring partner</span>
              </button>
            </div>

            {!loading && clients.length === 0 && (
              <div className="border border-[var(--line-strong)] bg-[var(--bg-2)] py-24 text-center">
                <p className="font-serif-accent italic text-[var(--accent)] text-2xl mb-2">
                  No hiring partners yet.
                </p>
                <p className="text-[var(--ink-80)]">
                  Create an account so a client can see their own shortlist and match scores.
                </p>
              </div>
            )}

            {clients.length > 0 && (
              <div className="border border-[var(--line-strong)]">
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--line-strong)] text-[10px] uppercase tracking-[0.24em] text-[var(--ink-50)] bg-[var(--bg-2)]">
                  <div className="col-span-3">Person</div>
                  <div className="col-span-3">Company</div>
                  <div className="col-span-2">Assigned roles</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                {clients.map((c) => (
                  <div
                    key={c.id}
                    data-testid={ADMIN_CLIENTS.row(c.id)}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 border-b border-[var(--line)] hover:bg-[var(--bg-2)] transition-colors"
                  >
                    <div className="col-span-3">
                      <p className="text-[var(--ink)] font-medium">{c.name}</p>
                      <a
                        href={`mailto:${c.email}`}
                        className="text-[var(--ink-70)] text-sm break-all"
                      >
                        {c.email}
                      </a>
                    </div>
                    <div className="col-span-3">
                      <p className="text-[var(--ink)] inline-flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-[var(--ink-70)]" />
                        {c.company || "—"}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[var(--ink)] inline-flex items-center gap-1.5">
                        <Briefcase className="w-3.5 h-3.5 text-[var(--ink-70)]" />
                        {(c.assigned_job_ids || []).length}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <button
                        onClick={() => onToggleActive(c)}
                        className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${
                          c.is_active
                            ? "border-[var(--accent)]/40 text-[var(--accent)]"
                            : "border-[var(--line-strong)] text-[var(--ink-50)]"
                        }`}
                      >
                        {c.is_active ? (
                          <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" /> Active</span>
                        ) : (
                          <span className="inline-flex items-center gap-1"><Ban className="w-3 h-3" /> Disabled</span>
                        )}
                      </button>
                    </div>
                    <div className="col-span-2 md:text-right flex md:justify-end items-center gap-2">
                      <button
                        onClick={() => setClientEditor({ open: true, initial: c })}
                        className="p-2 text-[var(--ink-70)] hover:text-[var(--accent)]"
                        data-testid={ADMIN_CLIENTS.edit(c.id)}
                        aria-label="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeleteClient(c)}
                        className="p-2 text-[var(--ink-70)] hover:text-red-600"
                        data-testid={ADMIN_CLIENTS.delete(c.id)}
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Blog tab */}
        {tab === "blog" && (
          <div data-testid={ADMIN_BLOG.list}>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setBlogEditor({ open: true, initial: null })}
                className="btn-pill text-sm"
                data-testid={ADMIN_BLOG.create}
              >
                <Plus className="w-4 h-4" />
                <span>New essay</span>
              </button>
            </div>

            {!loading && posts.length === 0 && (
              <div className="border border-[var(--line-strong)] bg-[var(--bg-2)] py-24 text-center">
                <p className="font-serif-accent italic text-[var(--accent)] text-2xl mb-2">
                  No essays yet.
                </p>
                <p className="text-[var(--ink-80)]">
                  Publish the first Insights post to seed the Insights page.
                </p>
              </div>
            )}

            {posts.length > 0 && (
              <div className="border border-[var(--line-strong)]">
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--line-strong)] text-[10px] uppercase tracking-[0.24em] text-[var(--ink-50)] bg-[var(--bg-2)]">
                  <div className="col-span-6">Title</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Published</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                {posts.map((p) => (
                  <div
                    key={p.id}
                    data-testid={ADMIN_BLOG.row(p.id)}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 border-b border-[var(--line)] hover:bg-[var(--bg-2)] transition-colors"
                  >
                    <div className="col-span-6">
                      <p className="text-[var(--ink)] font-medium">{p.title}</p>
                      <p className="text-[var(--ink-70)] text-xs line-clamp-1 mt-1">{p.excerpt}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(p.tags || []).slice(0, 4).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-70)] border border-[var(--line-strong)] px-1.5 py-0.5">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span
                        className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${
                          p.is_published
                            ? "border-[var(--accent)]/40 text-[var(--accent)]"
                            : "border-[var(--line-strong)] text-[var(--ink-50)]"
                        }`}
                      >
                        {p.is_published ? "Live" : "Draft"}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[var(--ink-70)] text-xs">
                        {p.published_at ? formatDate(p.published_at) : "—"}
                      </p>
                    </div>
                    <div className="col-span-2 md:text-right flex md:justify-end items-center gap-2">
                      <a
                        href={`/insights/${p.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-70)] hover:text-[var(--accent)] link-slide"
                      >
                        View
                      </a>
                      <button
                        onClick={() => setBlogEditor({ open: true, initial: p })}
                        className="p-2 text-[var(--ink-70)] hover:text-[var(--accent)]"
                        data-testid={ADMIN_BLOG.edit(p.id)}
                        aria-label="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeletePost(p)}
                        className="p-2 text-[var(--ink-70)] hover:text-red-600"
                        data-testid={ADMIN_BLOG.delete(p.id)}
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      <ClientEditorModal
        open={clientEditor.open}
        initial={clientEditor.initial}
        onClose={() => setClientEditor({ open: false, initial: null })}
        onSaved={onClientSaved}
      />
      <BlogEditorModal
        open={blogEditor.open}
        initial={blogEditor.initial}
        onClose={() => setBlogEditor({ open: false, initial: null })}
        onSaved={onPostSaved}
      />
    </div>
  );
}
